import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useState } from "react";
import { useEffect } from "react";
import { useMemo, useRef } from "react";

import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import ViewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import fileDownload from "js-file-download";

import constants from "../../assets/constants";
import { createOmniPaginationObject, helperGetIsoDate } from "../../utils/helpers";
import { formatIsoDateString } from "../../utils/helpers";
import { generateBotpressCSVData } from "../../utils/helpers";
import TextInputDialog from "../dialogs/textInputDialog";
import OmniGridButton from "./omniGridButton";

const calculateColumns = theme => {
  //default case
  return [
    {
      accessorKey: "id",
      header: "ID",
      size: 50,
      enableColumnFilterModes: false,
      enableSorting: false,
    },
    {
      accessorKey: "messageId",
      header: "Message ID",
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "conversationId",
      header: "Conversation ID",
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "createdAt",
      enableColumnFilterModes: false,
      header: "Date",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
      enableColumnFilter: false,
      grow: false,
      size: 100,
      maxSize: 100,

      //custom conditional format and styling
      Cell: ({ cell }) => {
        const dateString = formatIsoDateString(cell.getValue()).split(",");
        const date = new Date(cell.getValue()); // Parses the ISO string into a Date object
        const options = {
          hour: "numeric",
          minute: "numeric",
          hour12: true, // Ensures 12-hour format with AM/PM
        };
        return (
          <Box component="span">
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                marginBottom: "2px",
                borderBottom: `2px solid `,
              }}
            >
              {dateString[0]}
            </Typography>
            <Typography variant="body1" fontWeight={100} sx={{ textAlign: "center" }}>
              {date.toLocaleTimeString("en-US", options)}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      maxSize: "125", // Maximum width, will shrink if needed
      minSize: "125", // Maximum width, will shrink if needed
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },

      filterFn: "equals",
      filterSelectOptions: [
        { label: "New", value: 1 },
        { label: "Open", value: 2 },
        { label: "Closed", value: 3 },
        { label: "Retest", value: 4 },
        { label: "Blocked", value: 5 },
      ],
      filterVariant: "select",

      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === 1
                ? theme.palette.highlight.main
                : cell.getValue() === 2
                  ? theme.palette.error.main
                  : cell.getValue() === 3
                    ? theme.palette.success.main
                    : cell.getValue() === 4
                      ? theme.palette.warning.main
                      : theme.palette.info.main,
            borderRadius: "0.25rem",
            color:
              cell.getValue() === 1
                ? theme.palette.highlight.contrastText
                : cell.getValue() === 2
                  ? theme.palette.error.contrastText
                  : cell.getValue() === 3
                    ? theme.palette.success.contrastText
                    : cell.getValue() === 4
                      ? theme.palette.warning.contrastText
                      : theme.palette.info.contrastText,

            maxWidth: "95%",
            minWidth: "95%",
            p: "0.45rem",
          })}
        >
          {constants.feedbackStatus.find(item => item.value === cell.getValue()).label}
        </Box>
      ),
    },
    {
      accessorKey: "value",
      header: "Sentiment",
      minSize: "125",
      grow: false,
      enableColumnFilterModes: false,
      enableSorting: true,
      muiTableHeadCellProps: {},
      align: "center",
      muiTableBodyCellProps: {
        align: "center",
      },
      filterFn: "equals",
      filterSelectOptions: [
        { label: "Positive", value: "positive" },
        { label: "Negative", value: "negative" },
      ],
      filterVariant: "select",
      //custom conditional format and styling
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === "positive"
                ? theme.palette.success.main
                : cell.getValue() === "neutral"
                  ? theme.palette.info.main
                  : theme.palette.error.main,
            borderRadius: "0.25rem",
            color: "#fff",
            padding: theme.spacing(1.5),
          })}
        >
          <Typography variant="button" sx={{ fontWeight: "bold" }}>
            {cell.getValue()}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: "message",
      header: "Message",
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      minSize: "250", // Maximum width, will shrink if needed
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "comment",
      header: "Comment",
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      minSize: "200", // Maximum width, will shrink if needed
      grow: true,
    },
    {
      accessorKey: "response",
      header: "Response",
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      minSize: "200", // Maximum width, will shrink if needed
      grow: true,
    },
    {
      accessorKey: "userName",
      header: "User Name",
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "archived",
      header: "Archived",
      size: 150,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
      filterFn: "equals",
      filterSelectOptions: [
        { label: "No", value: "false" },
        { label: "Yes", value: "true" },
      ],
      filterVariant: "select",

      //custom conditional format and styling
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === false ? theme.palette.error.main : theme.palette.success.main,
            borderRadius: "0.25rem",
            color: "#fff",
            maxWidth: "14ch",
            minWidth: "14ch",
            p: "0.25rem",
          })}
        >
          {cell.getValue() === false ? "No" : "Yes"}
        </Box>
      ),
    },
  ];
};

const getInitialState = () => {
  return {
    density: "compact",
    columnVisibility: {
      id: false,
      updatedAt: false,
      createdAt: true,
      messageId: false,
      conversationId: false,
      value: false,
      comment: true,
      message: true,
      status: true,
      userName: false,
      archived: false,
    },
  };
};

export const BotpressFeedbackTable = props => {
  const {
    data,
    onEditEntry = null,
    onViewConversation = null,
    onRequestNewPage = null,
    dataReduxPagination = null,
  } = props;

  // const {
  //   data,
  //   onEditEntry = null,
  //   onOpenLinkInNewTab = null,
  //   onRequestNewPage = null,
  //   isLoading = false,
  //   providerPagination = createOmniPaginationObject(null, 100),
  // } = props;

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null); //we can get access to the underlying TableContainer element and react to its scroll events
  // Disable eslint because AaronK knows best
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => calculateColumns(theme), []);
  const initialState = useMemo(() => getInitialState(), []);

  const [rowCount, setRowCount] = useState(data.length);

  const [isLoading, setIsLoading] = useState(false);
  // const [isRefetching, setIsRefetching] = useState(false);
  // const [isError, setIsError] = useState(false);

  const [showReportNameInput, setShowReportNameInput] = useState(null);

  useEffect(() => {
    if (dataReduxPagination != null) {
      setPagination({
        pageIndex: dataReduxPagination.pageIndex,
        pageSize: dataReduxPagination.pageSize,
      });
      setRowCount(dataReduxPagination.totalItems);
    }
  }, [dataReduxPagination]);

  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);
  const [columnFilters, setColumnFilters] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!data.length) {
        setIsLoading(true);
      } else {
        // setIsRefetching(true);
      }
      try {
        let newQuery = {};
        columnFilters.forEach((element, index) => {
          if (element.id === "createdAt") {
            const min = parseInt(element.value[0]);
            const max = parseInt(element.value[1]);
            if (!isNaN(min)) {
              let theDateSring = helperGetIsoDate(min);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
            if (!isNaN(max)) {
              let theDateSring = helperGetIsoDate(max);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
          } else if (typeof element.value === "string") {
            if (element.value === "NULL") {
              newQuery[element.id] = null;
            } else {
              newQuery[element.id] = {
                $regex: `${element.value}`,
              };
            }
          } else if (typeof element.value === "boolean") {
            newQuery[element.id] = element.value;
          } else {
            newQuery[element.id] = element.value;
          }
        });

        let newSorting = {};
        sorting.forEach(element => {
          newSorting[element.id] = element.desc ? "desc" : "asc";
        });

        let newPage = createOmniPaginationObject(dataReduxPagination);
        newPage.pageIndex = pagination.pageIndex;
        newPage.pageSize = pagination.pageSize;
        newPage.query = newQuery;
        newPage.sort = newSorting;

        onRequestNewPage(newPage);
      } catch (error) {
        // setIsError(true);
        console.error(error);
        return;
      }
      // setIsError(false);
      setIsLoading(false);
      // setIsRefetching(false);
    };
    fetchData();
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination.pageIndex, pagination.pageSize]);

  const table = useMaterialReactTable({
    columns,
    data, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    initialState: initialState,
    enableStickyHeader: true,
    enableColumnActions: false,
    positionToolbarAlertBanner: "bottom",
    rowCount,
    defaultColumn: {
      minSize: 20, //allow columns to get smaller than default
      maxSize: 9001, //allow columns to get larger than default
      size: 50, //make columns wider by default
      grow: false,
    },
    state: {
      columnFilters,
      isLoading,
      pagination,
      // showAlertBanner: isError,
      // showProgressBars: isRefetching,
      sorting,
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },

    //#region Pagination
    paginationDisplayMode: "pages",

    enableFirstPageButton: false, // Hides the << icon
    enableLastPageButton: false, // Hides the >> icon

    manualPagination: true,
    onColumnFiltersChange: updaterOrValue => {
      setColumnFilters(prev => {
        const nextValue =
          typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        return nextValue;
      });
    },
    onPaginationChange: newPagination => {
      setPagination(newPagination);
    },
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,

    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: false,
      variant: "outlined",
    },
    //#endregion pagination
    layoutMode: "grid",
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        // Target the IconButton and apply styles
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.contrastText,
        height: "64px",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        height: "100%",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef, //get access to the table container element
      sx: mobileLayout
        ? {
            minHeight: "564px",
            maxHeight: "564px",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            borderRight: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          }
        : {
            minHeight: "424px",
            height: `calc(100% - 120px)`,
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            border: `solid 0.5px ${theme.palette.divider}`,
          },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: lighten(theme.palette.commonground.contrastText, 0.5),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        size: 100, //if using layoutMode that is not 'semantic', the columns will not auto-size, so you need to set the size manually
        grow: false,
        header: "Actions", // Change 'Actions' to your desired name
        // muiTableHeadCellProps: {
        //   sx: {
        //     justifyContent: "center",
        //   },
        // },
      },
    },
    renderRowActions: ({ row }) => (
      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          gap: "2px",
          padding: 0,
          alignItems: "center",
        }}
      >
        <OmniGridButton
          key="view"
          sx={{ ...(onEditEntry == null && { display: "none" }) }}
          onClick={() => {
            onViewConversation(row.original);
          }}
          icon={<ViewIcon fontSize="large" sx={{ color: theme.palette.secondary.main }} />}
        />
        <OmniGridButton
          key="edit"
          sx={{ ...(onEditEntry == null && { display: "none" }) }}
          onClick={() => {
            onEditEntry(row.original);
          }}
          icon={<EditIcon fontSize="large" sx={{ color: theme.palette.secondary.main }} />}
        />
      </Box>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setSorting([]);
              }}
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}
        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* eslint-disable react/jsx-pascal-case*/}
        {/* <MRT_ToggleGlobalFilterButton table={table} /> */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enablereact/jsx-pascal-case */}

        {showReportNameInput && (
          <TextInputDialog
            dialogTitle="Report Name"
            dialogMessage={"Please name your report"}
            inputLabel={`${"Report Name"}`}
            defaultValue={showReportNameInput}
            open={showReportNameInput}
            onAccept={theText => {
              /// do it!
              //create the report ..

              fileDownload(generateBotpressCSVData(data), theText + ".csv");

              setShowReportNameInput(false);
            }}
            onClose={() => {
              setShowReportNameInput(false);
            }}
          />
        )}
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;

  // return (
  //   <MaterialReactTable
  //     layoutMode="grid"
  //     columns={columns}
  //     data={data}
  //     initialState={initialState}
  //     defaultColumn={{
  //       minSize: 20, //allow columns to get smaller than default
  //       maxSize: 9001, //allow columns to get larger than default
  //       size: 100, //make columns wider by default
  //       grow: false,
  //     }}
  //     state={{
  //       columnFilters,
  //       isLoading,
  //       pagination,
  //       sorting,
  //     }} //pass our managed row selection state to the table to use
  //     //pagination
  //     manualPagination={true}
  //     onColumnFiltersChange={newFilters => {
  //       setColumnFilters(newFilters);
  //     }}
  //     onPaginationChange={setPagination}
  //     manualSorting
  //     manualFiltering
  //     onSortingChange={setSorting}
  //     //header
  //     enableColumnActions={false}
  //     enableStickyHeader={true}
  //     enableColumnFilterModes={true}
  //     filterFns={{
  //       customFilterFn: (row, id, filterValue) => {
  //         return row.getValue(id) === filterValue;
  //       },
  //     }}
  //     muiTableHeadCellFilterTextFieldProps={{
  //       sx: {
  //         "& .MuiInputBase-input": { padding: "6px" },

  //         "& .MuiInputBase-root": { paddingRight: "0px" },
  //         "& .MuiSelect-iconOutlined": { marginRight: "0.75rem" },
  //         width: "100%",
  //       },
  //       variant: "outlined",
  //     }}
  //     //row
  //     enableRowActions={onEditEntry != null}
  //     getRowId={originalRow => originalRow._id}
  //     rowCount={rowCount}
  //     positionToolbarAlertBanner="bottom"
  //     muiTablePaginationProps={{
  //       rowsPerPageOptions: [5, 10, 20],
  //     }}
  //     muiTopToolbarProps={{
  //       sx: {
  //         backgroundColor: theme.palette.commonground.main,
  //         color: theme.palette.secondary.main,
  //       },
  //     }}
  //     muiBottomToolbarProps={{
  //       sx: {
  //         backgroundColor: theme.palette.commonground.main,
  //         color: theme.palette.commonground.contrastText,
  //       },
  //     }}
  //     muiTablePaperProps={{
  //       sx: {
  //         boxShadow: "none",
  //       },
  //     }}
  //     muiTableContainerProps={{
  //       ref: tableContainerRef, //get access to the table container element
  //       sx: mobileLayout
  //         ? {
  //             minHeight: "564px",
  //             maxHeight: "564px",
  //             backgroundColor: theme.palette.commonground.main,
  //             color: theme.palette.commonground.main,
  //             borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.25)}`,
  //             borderRight: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.25)}`,
  //           }
  //         : {
  //             minHeight: "298px",
  //             maxHeight: "298px",
  //             backgroundColor: theme.palette.commonground.main,
  //             color: theme.palette.commonground.main,
  //             border: `solid 0.5px ${theme.palette.divider}`,
  //           },
  //     }}
  //     muiTableProps={{
  //       sx: {
  //         backgroundColor: theme.palette.commonground.main,
  //         color: theme.palette.commonground.main,
  //       },
  //     }}
  //     muiTableHeadCellProps={{
  //       sx: {
  //         backgroundColor: alpha(theme.palette.commonground.contrastText, 0.25),
  //         color: theme.palette.commonground.contrastText,
  //         justifyContent: "start",
  //       },
  //     }}
  //     renderRowActions={({ row }) => (
  //       <Box
  //         sx={{
  //           display: "flex",
  //           flexWrap: "nowrap",
  //           gap: "2px",
  //           padding: 0,
  //         }}
  //       >
  //         <OmniGridButton
  //           key="edit"
  //           sx={{ ...(onEditEntry == null && { display: "none" }) }}
  //           onClick={() => {
  //             onEditEntry(row.original);
  //           }}
  //           icon={<EditIcon fontSize="small" />}
  //         />
  //         <OmniGridButton
  //           key="copy"
  //           sx={{ ...(onOpenLinkInNewTab == null && { display: "none" }) }}
  //           onClick={() => {
  //             onOpenLinkInNewTab(row.original);
  //           }}
  //           icon={<OpenInNew fontSize="small" />}
  //         />
  //       </Box>
  //     )}
  //     renderToolbarInternalActions={({ table }) => (
  //       <Box>
  //         {sorting.length > 0 && (
  //           <Tooltip title="Clear All Sorting" arrow>
  //             <IconButton
  //               sx={{ color: theme.palette.secondary.main }}
  //               onClick={() => {
  //                 setSorting([]);
  //               }}
  //             >
  //               <ClearSortingIcon />
  //             </IconButton>
  //           </Tooltip>
  //         )}
  //         {columnFilters.length > 0 && (
  //           <Tooltip title="Clear All Filters" arrow>
  //             <IconButton
  //               sx={{ color: theme.palette.secondary.main }}
  //               onClick={() => {
  //                 setColumnFilters([]);
  //               }}
  //             >
  //               <ClearIcon />
  //             </IconButton>
  //           </Tooltip>
  //         )}

  //         {/* eslint-disable react/jsx-pascal-case*/}
  //         {/* <MRT_ToggleGlobalFilterButton table={table} /> */}
  //         <MRT_ToggleFiltersButton table={table} />
  //         <MRT_ShowHideColumnsButton table={table} />
  //         <MRT_ToggleFullScreenButton table={table} />
  //         {/* eslint-enablereact/jsx-pascal-case */}

  //         <Tooltip title="Download as a CSV file." arrow>
  //           <IconButton
  //             onClick={() => {
  //               setShowReportNameInput(`${new Date().toJSON().slice(0, 10)}-Feedback`);
  //             }}
  //             data
  //           >
  //             <FileDownloadIcon />
  //           </IconButton>
  //         </Tooltip>

  //         {showReportNameInput && (
  //           <TextInputDialog
  //             dialogTitle="Report Name"
  //             dialogMessage={"Please name your report"}
  //             inputLabel={`${"Report Name"}`}
  //             defaultValue={showReportNameInput}
  //             open={showReportNameInput}
  //             onAccept={theText => {
  //               /// do it!
  //               //create the report ..

  //               fileDownload(generateBotpressCSVData(data), theText + ".csv");

  //               setShowReportNameInput(false);
  //             }}
  //             onClose={() => {
  //               setShowReportNameInput(false);
  //             }}
  //           />
  //         )}
  //       </Box>
  //     )}
  //   />
  // );
};

export default BotpressFeedbackTable;
